import { useCallback, useEffect, useState } from 'react'
import { BellRing, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'
import type { Translations } from '@/i18n/translations'

interface Pending { id: string; name: string; avatar_url: string; tentative: boolean }

interface ReminderState {
  pending: Pending[]
  reminded_at: number | null
  reminded_by: string
}

function ago(ts: number, now: number, T: Translations): string {
  const s = Math.max(0, now - ts)
  if (s < 60) return T.matches.agoJustNow
  if (s < 3600) return T.matches.agoMinutes.replace('{n}', String(Math.round(s / 60)))
  if (s < 86_400) return T.matches.agoHours.replace('{n}', String(Math.round(s / 3600)))
  return T.matches.agoDays.replace('{n}', String(Math.round(s / 86_400)))
}

function Group({ label, people }: { label: string; people: Pending[] }) {
  if (people.length === 0) return null
  return (
    <div className="mt-3">
      <p className="mb-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground">
        {label} <span className="tabular-nums text-muted-foreground/60">· {people.length}</span>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {people.map(p => (
          <span key={p.id} className="flex items-center gap-1.5 rounded-sm border border-white/10 bg-black/20 py-0.5 pl-0.5 pr-2 text-xs text-foreground/80">
            <img src={p.avatar_url} alt="" className="h-5 w-5 rounded-sm" loading="lazy" />
            {p.name}
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * Under the sign-up board: the clan members who still owe the match an
 * answer, split into the undecided and those who never answered. Everyone sees
 * the full list; only staff get the reminder button (DM, panel notification
 * and a tag in the match channel).
 *
 * Sending asks once more, and the last send is shown so a second organiser
 * does not repeat it an hour later.
 */
export default function SignUpReminder({ eventId, signUpKey, canSend }: {
  eventId: string
  /** Changes whenever a sign-up is added, removed or switches class, so the
   *  list follows the board — undecided → a position leaves the list too. */
  signUpKey: string
  canSend: boolean
}) {
  const { T } = useLanguage()
  const [state, setState] = useState<ReminderState | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const load = useCallback(() => {
    fetch(`/api/matches/${eventId}/reminder`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(setState)
      .catch(() => setState(null))
  }, [eventId])

  useEffect(() => { load() }, [load, signUpKey])
  useEffect(() => { setMessage(null); setConfirming(false) }, [eventId])

  if (!state) return null
  const n = state.pending.length
  const undecided = state.pending.filter(p => p.tentative)
  const unanswered = state.pending.filter(p => !p.tentative)

  const send = async () => {
    setBusy(true)
    setMessage(null)
    try {
      const res = await fetch(`/api/matches/${eventId}/reminder`, { method: 'POST', credentials: 'include' })
      const d = await res.json().catch(() => ({}))
      if (!res.ok) {
        setMessage({ ok: false, text: T.matches.reminderError })
        return
      }
      setMessage({ ok: true, text: T.matches.reminderSent.replace('{n}', String(d.sent ?? n)) })
      setConfirming(false)
      load()
    } catch {
      setMessage({ ok: false, text: T.matches.reminderError })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-4 rounded-sm border border-white/10 bg-white/[0.035] p-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-foreground/70">
            <BellRing className="h-3.5 w-3.5" />
            {T.matches.reminderTitle}
            <span className="font-sans text-xs font-normal normal-case tracking-normal tabular-nums text-muted-foreground">
              {T.matches.reminderCount.replace('{n}', String(n))}
            </span>
          </p>
          {state.reminded_at && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              {T.matches.reminderLast
                .replace('{ago}', ago(state.reminded_at, Date.now() / 1000, T))
                .replace('{name}', state.reminded_by || '-')}
            </p>
          )}
        </div>
        {canSend && n > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {confirming ? (
              <>
                <span className="text-xs text-foreground/80">
                  {T.matches.reminderConfirm.replace('{n}', String(n))}
                </span>
                <Button size="sm" className="h-8 gap-1.5 text-xs" disabled={busy} onClick={send}>
                  <Send className="h-3.5 w-3.5" />{T.matches.reminderConfirmYes}
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={busy} onClick={() => setConfirming(false)}>
                  {T.matches.reminderCancel}
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" disabled={busy} onClick={() => setConfirming(true)}>
                <BellRing className="h-3.5 w-3.5" />{T.matches.reminderSend.replace('{n}', String(n))}
              </Button>
            )}
          </div>
        )}
      </div>

      {n > 0 ? (
        <>
          {canSend && <p className="mt-2 text-xs text-muted-foreground">{T.matches.reminderHint}</p>}
          <Group label={T.matches.reminderGroupUndecided} people={undecided} />
          <Group label={T.matches.reminderGroupNone} people={unanswered} />
        </>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">{T.matches.reminderAllAnswered}</p>
      )}

      {message && (
        <p className={`mt-2 text-xs ${message.ok ? 'text-foreground/80' : 'text-primary'}`}>{message.text}</p>
      )}
    </div>
  )
}
